import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import api from '../../../connections/api';
import routes_api from '../../../connections/routes_api';
import { formatDate, toDatePart } from '../../../shared_items/formatConventions';

const ORIGINS = [
  { id: 'ui', label: 'UI' },
  { id: 'mcp', label: 'MCP' },
  { id: 'admin', label: 'Admin' },
];

const MODULES = [
  { id: 'accounts', label: 'Accounts' },
  { id: 'core', label: 'Core' },
  { id: 'finance', label: 'Finance' },
  { id: 'health', label: 'Health' },
  { id: 'identity', label: 'Identity' },
  { id: 'notepad', label: 'Notepad' },
  { id: 'planner', label: 'Planner' },
  { id: 'travel', label: 'Travel' },
];

const MODEL_LABELS = {
  'accounts.customuser': 'Profile',
  'accounts.userapppreferences': 'App preferences',
  'agent_api.agentapikey': 'Assistant key',
};

const MODULE_CHIPS = {
  core: { label: 'Core', className: 'bg-module-core text-white' },
  health: { label: 'Health', className: 'bg-module-health text-white' },
  travel: { label: 'Travel', className: 'bg-module-travel text-white' },
  planner: { label: 'Planner', className: 'bg-module-planner text-white' },
  finance: { label: 'Finance', className: 'bg-module-finance text-white' },
  notepad: { label: 'Notepad', className: 'bg-module-notepad text-white' },
  identity: { label: 'Identity', className: 'bg-module-identity text-white' },
  accounts: { label: 'Accounts', className: 'bg-module-accounts text-white' },
  agent_api: { label: 'Accounts', className: 'bg-module-accounts text-white' },
};

function moduleChip(modelLabel) {
  const app = String(modelLabel || '').split('.')[0];
  return MODULE_CHIPS[app] || {
    label: app ? app.charAt(0).toUpperCase() + app.slice(1) : '—',
    className: 'bg-gray-700 text-white',
  };
}

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = formatDate(toDatePart(typeof value === 'string' ? value : date.toISOString()));
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const tz = new Intl.DateTimeFormat('en-US', { timeZoneName: 'shortOffset' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;
  return `${day}, ${time}${tz ? ` ${tz}` : ''}`;
}

function formatValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const HIDDEN_FIELD = /password|secret|token|api_?key|key_hash/i;
const NOISE_FIELD = /^(id|pk|user_id|created_at|updated_at)$/;
const IDENTIFYING_FIELDS = [
  'name', 'username', 'email', 'app_key', 'app_visible', 'app_order', 'app_initial', 'scopes', 'is_active',
];

function ChangeLines({ row }) {
  const created = row.action === 'create' || row.action === 'bulk_create';
  const fields = created
    ? Object.keys(row.after || {}).filter((field) => (
      !NOISE_FIELD.test(field)
      && !HIDDEN_FIELD.test(field)
      && row.after[field] != null
      && row.after[field] !== ''
    ))
    : (row.changed_fields?.length
      ? row.changed_fields
      : IDENTIFYING_FIELDS.filter((field) => row.before?.[field] != null || row.after?.[field] != null)
    ).filter((field) => !HIDDEN_FIELD.test(field));
  if (!fields.length) return null;

  return (
    <ul className="mt-2 space-y-1 text-xs text-gray-600">
      {fields.map((field) => (
        <li key={field}>
          <span className="font-medium text-gray-700">{field}</span>
          {': '}
          {created
            ? formatValue(row.after?.[field])
            : `${formatValue(row.before?.[field])} → ${formatValue(row.after?.[field])}`}
        </li>
      ))}
    </ul>
  );
}

function AuditRecordCard({ row }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef(null);
  const chip = moduleChip(row.model_label);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [row, expanded]);

  return (
    <li className="border rounded-lg bg-gray-50">
      <div className="relative">
        <div ref={bodyRef} className={expanded ? 'p-3' : 'max-h-20 overflow-hidden p-3'}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium">
              {row.source && (
                <span className="inline-flex shrink-0 rounded-full bg-module-accounts/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-module-accounts">
                  {row.source}
                </span>
              )}
              <span className={`inline-flex shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${chip.className}`}>
                {chip.label}
              </span>
              {row.action} · {MODEL_LABELS[row.model_label] || row.model_label}
            </span>
            <span className="text-xs text-gray-500">{formatWhen(row.created_at)}</span>
          </div>
          <ChangeLines row={row} />
        </div>
        {!expanded && overflows && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-gray-50 to-transparent" />
        )}
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="px-3 pb-2 text-left text-[11px] font-medium text-gray-500 hover:text-gray-800"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </li>
  );
}

function DisplayOptions({ hideMcpLastUsed, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Display options"
        aria-expanded={open}
        className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
      >
        <Eye className="h-4 w-4" strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-gray-200 bg-white py-1 shadow">
          <button
            type="button"
            onClick={() => onChange(!hideMcpLastUsed)}
            className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
          >
            <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded border ${
              hideMcpLastUsed ? 'border-module-accounts bg-module-accounts text-white' : 'border-gray-300'
            }`}
            >
              {hideMcpLastUsed && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            Hide MCP last_used_at
          </button>
        </div>
      )}
    </div>
  );
}

function sameIds(left, right) {
  if (left.length !== right.length) return false;
  const ids = new Set(left);
  return right.every((id) => ids.has(id));
}

function ModuleFilter({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);
  const ref = useRef(null);
  const allIds = MODULES.map((module) => module.id);
  const allChecked = draft.length === MODULES.length;
  const label = selected.length === MODULES.length
    ? 'All modules'
    : MODULES.filter((module) => selected.includes(module.id)).map((module) => module.label).join(', ')
      || 'Module';

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const toggleOpen = () => {
    setOpen((value) => {
      if (!value) setDraft(selected);
      return !value;
    });
  };

  const toggle = (id) => {
    setDraft((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const apply = () => {
    if (!sameIds(draft, selected)) onChange(draft);
    setOpen(false);
  };

  const box = (checked) => (
    <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded border ${
      checked ? 'border-module-accounts bg-module-accounts text-white' : 'border-gray-300'
    }`}
    >
      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
    </span>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggleOpen}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow">
          <button
            type="button"
            onClick={() => setDraft(allChecked ? [] : allIds)}
            className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            {box(allChecked)}
            Select all
          </button>
          {MODULES.map((module) => (
            <button
              key={module.id}
              type="button"
              onClick={() => toggle(module.id)}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
            >
              {box(draft.includes(module.id))}
              {module.label}
            </button>
          ))}
          <div className="border-t border-gray-100 p-1">
            <button
              type="button"
              onClick={apply}
              disabled={sameIds(draft, selected)}
              className="w-full rounded bg-module-accounts px-2 py-1 text-xs text-white hover:bg-module-accounts/90 disabled:opacity-40"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OriginFilter({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);
  const ref = useRef(null);
  const allIds = ORIGINS.map((origin) => origin.id);
  const allChecked = draft.length === ORIGINS.length;
  const label = selected.length === ORIGINS.length
    ? 'All origins'
    : ORIGINS.filter((origin) => selected.includes(origin.id)).map((origin) => origin.label).join(', ')
      || 'Origin';

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const toggleOpen = () => {
    setOpen((value) => {
      if (!value) setDraft(selected);
      return !value;
    });
  };

  const toggle = (id) => {
    setDraft((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const apply = () => {
    if (!sameIds(draft, selected)) onChange(draft);
    setOpen(false);
  };

  const box = (checked) => (
    <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded border ${
      checked ? 'border-module-accounts bg-module-accounts text-white' : 'border-gray-300'
    }`}
    >
      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
    </span>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggleOpen}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow">
          <button
            type="button"
            onClick={() => setDraft(allChecked ? [] : allIds)}
            className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            {box(allChecked)}
            Select all
          </button>
          {ORIGINS.map((origin) => (
            <button
              key={origin.id}
              type="button"
              onClick={() => toggle(origin.id)}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
            >
              {box(draft.includes(origin.id))}
              {origin.label}
            </button>
          ))}
          <div className="border-t border-gray-100 p-1">
            <button
              type="button"
              onClick={apply}
              disabled={sameIds(draft, selected)}
              className="w-full rounded bg-module-accounts px-2 py-1 text-xs text-white hover:bg-module-accounts/90 disabled:opacity-40"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const ViewAccountAudit = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sources, setSources] = useState(ORIGINS.map((origin) => origin.id));
  const [modules, setModules] = useState(MODULES.map((module) => module.id));
  const [hideMcpLastUsed, setHideMcpLastUsed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const params = {
      page,
      hide_mcp_last_used: hideMcpLastUsed ? '1' : '0',
    };
    if (sources.length < ORIGINS.length) params.source = sources.join(',');
    if (modules.length < MODULES.length) params.module = modules.join(',');
    api.get(routes_api.accounts_audit_log, { params })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data?.results || []);
        setCount(res.data?.count || 0);
        setPages(res.data?.pages || 1);
        setPage((current) => (current === res.data?.page ? current : (res.data?.page || 1)));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, sources, modules, hideMcpLastUsed]);

  const changeSources = (next) => {
    setPage(1);
    setSources(next);
  };

  const changeModules = (next) => {
    setPage(1);
    setModules(next);
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow text-gray-700">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          Records
          {loading ? (
            <span role="status" aria-label="Loading records" className="inline-flex items-center gap-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-[gs-loading-dot-pulse_1.05s_ease-in-out_infinite]"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </span>
          ) : (
            <span>({rows.length} of {count})</span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <DisplayOptions
            hideMcpLastUsed={hideMcpLastUsed}
            onChange={(next) => {
              setPage(1);
              setHideMcpLastUsed(next);
            }}
          />
          <OriginFilter selected={sources} onChange={changeSources} />
          <ModuleFilter selected={modules} onChange={changeModules} />
          <div className="inline-flex items-center gap-1 text-xs text-gray-600">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              disabled={page <= 1 || loading}
              className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            </button>
            <span className="min-w-[3.5rem] text-center">{page} / {pages}</span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(current + 1, pages))}
              disabled={page >= pages || loading}
              className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="border-4 border-gray-300 border-t-gray-700 rounded-full w-8 h-8 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-sm text-gray-500">Could not load the audit log.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No account changes yet.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <AuditRecordCard key={row.id} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default ViewAccountAudit;
